import React, { useCallback, useMemo, useRef, useState } from 'react';
import UnifiedTvControl from '../../components/Controls/UnifiedTvControl';
import { UNIFIED_TV_COMMANDS } from '../../components/Controls/unifiedTvControlCommands';
import KaraokeRemotePanel from '../../components/Remote/KaraokeRemotePanel';
import RemotePwaSupport from '../../components/Remote/RemotePwaSupport';
import useSharedRemoteRuntime, {
  PREVIEW_PRESETS,
} from '../../components/Remote/useSharedRemoteRuntime';
import {
  getRemoteTarget,
  getRemoteJukeboxRuntime,
  getRemoteKaraokeRuntime,
  listRemoteContentTargets,
  executeRemoteContentTarget,
  requestRemoteState,
  sendRemoteAssistantCommand,
  sendRemoteAndroidTvAction,
  openRemoteAndroidBluetoothSettings,
  sendRemoteProtocolCommand,
  sendRemoteRoutedAction,
  sendRemoteSocketCommand,
} from '../../components/Remote/deviceRemoteApi';
import useRemoteSurfaceSession, {
  buildSyntheticResponse,
  createLocalEmitter,
  normalizeInstalledApps,
} from '../../components/Remote/useRemoteSurfaceSession';
import './consoleApp.css';

const formatPreviewInterval = (value) => {
  const seconds = Number(value || 0) / 1000;
  if (!seconds) return 'Off';
  return `${seconds}s`;
};

export default function ConsoleApp() {
  const emitterRef = useRef(createLocalEmitter());
  const screensRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [karaokeAvailable, setKaraokeAvailable] = useState(false);
  const [jukeboxAvailable, setJukeboxAvailable] = useState(false);
  const [activeQueuePlugin, setActiveQueuePlugin] = useState('karaoke');
  const {
    activeTarget,
    bootMessage,
    bootStatus,
    device,
    loadTarget,
    remoteDevice,
    selectedTargetId,
    selectTarget,
    setDevice,
    targetLoading,
    targets,
  } = useRemoteSurfaceSession({
    expectedRoute: '/console',
    targetStorageKeyPrefix: 'consoleRemote',
    loadingMessage: 'Loading your console remote…',
    errorMessage: 'Failed to load your console remote.',
    emptyMessage: 'No controllable devices are available on this account yet.',
  });

  const dispatchSocketCommand = useCallback(({ deviceId, command, value, id }) => {
    sendRemoteSocketCommand({
      targetDeviceId: deviceId,
      command,
      value,
    })
      .then((data) => {
        emitterRef.current.emitResponse(
          buildSyntheticResponse({
            deviceId,
            command,
            value,
            requestId: id,
            data,
          })
        );
      })
      .catch((error) => {
        if (error?.status === 404 || error?.status === 409) {
          emitterRef.current.emitMissing({
            deviceId,
            requestId: id,
            command,
            reason: error.status === 409 ? 'native_channel_missing' : 'device_not_connected',
            message: error.message,
          });
          return;
        }

        emitterRef.current.emitResponse({
          deviceId,
          type: `${command}_response`,
          requestId: id,
          success: false,
          error: error.message || 'Command failed',
        });
      });

    return id;
  }, []);

  const transport = useMemo(() => ({
    requestState: async ({ deviceId, includeScreenshot }) => {
      const data = await requestRemoteState({
        targetDeviceId: deviceId,
        includeScreenshot,
      });
      return data?.state || null;
    },
    loadApps: async ({ deviceId }) => {
      const data = await getRemoteTarget(deviceId, { includeApps: true });
      return normalizeInstalledApps(data?.target?.installedApps || []);
    },
    loadContentTargets: async ({ deviceId }) => {
      const data = await listRemoteContentTargets(deviceId);
      return data?.contentTargets || [];
    },
    executeContentTarget: ({ deviceId, contentTargetId }) =>
      executeRemoteContentTarget(deviceId, contentTargetId),
    sendProtocolCommand: async ({ deviceId, command, protocol }) => {
      const data = await sendRemoteProtocolCommand({
        targetDeviceId: deviceId,
        command,
        protocol,
      });
      return data?.result || data;
    },
    sendAssistantCommand: async ({ deviceId, command, params }) => {
      const data = await sendRemoteAssistantCommand({
        targetDeviceId: deviceId,
        command,
        params,
      });
      return { response: data?.response || '' };
    },
    sendRoutedAction: async ({ deviceId, ...payload }) =>
      sendRemoteRoutedAction({ targetDeviceId: deviceId, ...payload }),
    sendAndroidTvRemoteAction: ({ deviceId, ...payload }) =>
      sendRemoteAndroidTvAction({ targetDeviceId: deviceId, ...payload }),
    openBluetoothSettings: ({ deviceId }) => openRemoteAndroidBluetoothSettings({ targetDeviceId: deviceId }),
    sendSocketCommand: ({ deviceId, command, value, id }) =>
      dispatchSocketCommand({ deviceId, command, value, id }),
    subscribe: ({ onResponse, onMissing }) =>
      emitterRef.current.subscribe({ onResponse, onMissing }),
  }), [dispatchSocketCommand]);

  const {
    appsStatus,
    canPowerOff,
    canPowerOn,
    canPreviewSnapshot,
    canSwitchSource,
    contentTargetGroups,
    contentTargetsStatus,
    connectedMethodLabels,
    fetchLiveSnapshot,
    handlePrimaryRemoteCommand,
    handleRemoteInputEnd,
    handleExecuteContentTarget,
    handleLaunchSelectedApp,
    handleLaunchSuitestream,
    handleLgConnect,
    handleLgWake,
    handlePowerOff,
    handlePowerOn,
    handlePreviewPresetChange,
    handleSleepOnLan,
    handleSwitchSource,
    handleWakeOnLan,
    hdmiAssignments,
    inputDelayMs,
    isAppDevice,
    lastAction,
    liveSnapshot,
    liveSnapshotLabel,
    methodOptions,
    orderedApps,
    installedExternalAppCount,
    previewPreset,
    protectedVideoLikely,
    remoteAvailableCommands,
    selectedAppPackage,
    selectedMethod,
    selectedSource,
    setSelectedAppPackage,
    setSelectedMethod,
    setSelectedSource,
    showWolAction,
    snapshotMessage,
    snapshotStatus,
    statusMessage,
    targetExecution,
    backgroundIntervalMs,
  } = useSharedRemoteRuntime({
    device,
    onDeviceUpdated: setDevice,
    transport,
    previewEnabled: previewOpen,
  });

  const previewImageUrl = liveSnapshot?.screenshot?.dataUrl || '';

  React.useEffect(() => {
    let cancelled = false;

    const checkQueueAvailability = async () => {
      if (!remoteDevice?.id) {
        setKaraokeAvailable(false);
        setJukeboxAvailable(false);
        return;
      }

      const [karaokeResult, jukeboxResult] = await Promise.allSettled([
        getRemoteKaraokeRuntime(),
        getRemoteJukeboxRuntime(),
      ]);
      if (cancelled) return;
      const nextKaraokeAvailable = karaokeResult.status === 'fulfilled' && Boolean(karaokeResult.value?.karaoke?.enabled);
      const nextJukeboxAvailable = jukeboxResult.status === 'fulfilled' && Boolean(jukeboxResult.value?.jukebox?.enabled);
      setKaraokeAvailable(nextKaraokeAvailable);
      setJukeboxAvailable(nextJukeboxAvailable);
      setActiveQueuePlugin((current) => {
        if (current === 'jukebox' && nextJukeboxAvailable) return current;
        if (current === 'karaoke' && nextKaraokeAvailable) return current;
        return nextKaraokeAvailable ? 'karaoke' : nextJukeboxAvailable ? 'jukebox' : 'karaoke';
      });
    };

    checkQueueAvailability();

    return () => {
      cancelled = true;
    };
  }, [remoteDevice?.id]);

  React.useEffect(() => {
    if (bootStatus === 'loading') return;
    const frameId = window.requestAnimationFrame(() => {
      const screens = screensRef.current;
      if (screens) screens.scrollLeft = screens.scrollWidth;
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [bootStatus, jukeboxAvailable, karaokeAvailable]);

  if (bootStatus === 'loading') {
    return (
      <RemotePwaSupport surface="console">
        <div className="console-remote console-remote--loading">
          <section className="console-remote__loading-card">
            <div className="console-remote__eyebrow">Console Remote</div>
            <h1 className="console-remote__title">Preparing the operator surface</h1>
            <p className="console-remote__copy">{bootMessage}</p>
          </section>
        </div>
      </RemotePwaSupport>
    );
  }

  return (
    <RemotePwaSupport surface="console" promptTargetId="console-remote-install-prompt-slot">
      <div className="console-remote">
        <div className="console-remote__shell">
          <div className="console-remote__topbar">
            <div className="console-remote__brand">
              <span>Console</span>
              <strong>{activeTarget?.name || remoteDevice?.name || 'Remote'}</strong>
            </div>
            <div className="console-remote__top-actions">
              <span>{targets.length} targets</span>
              <button
                type="button"
                className="console-remote__hero-btn"
                onClick={() => loadTarget(selectedTargetId, { silent: false })}
                disabled={!selectedTargetId || targetLoading}
              >
                {targetLoading ? '...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="console-remote__screens" ref={screensRef}>
            <section className="console-remote__screen console-remote__screen--settings" aria-label="Settings">
              <div className="console-remote__screen-inner console-remote__settings-grid">
                <div className="console-remote__panel console-remote__target-strip">
                  {targets.map((target) => {
                    const isActive = String(target.id) === String(selectedTargetId);
                    return (
                      <button
                        key={target.id}
                        type="button"
                        className={`console-remote__target-card${isActive ? ' is-active' : ''}`}
                        onClick={() => selectTarget(target.id)}
                      >
                        <strong>{target.name}</strong>
                        <span>{[target.siteName, target.areaName].filter(Boolean).join(' • ') || 'Account-wide'}</span>
                        <small>{target.type || 'Unknown type'}</small>
                      </button>
                    );
                  })}
                </div>

                <div className="console-remote__panel console-remote__settings-panel">
                  {bootStatus === 'empty' ? <div className="console-remote__notice">{bootMessage}</div> : null}
                  {bootStatus === 'error' ? <div className="console-remote__notice console-remote__notice--error">{bootMessage}</div> : null}

                  <div className="console-remote__method-row">
                    {methodOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`console-remote__chip${selectedMethod === option.value ? ' is-active' : ''}`}
                        onClick={() => setSelectedMethod(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {selectedMethod === 'webos' ? (
                    <div className="console-remote__inline-actions">
                      <button type="button" className="console-remote__secondary-btn" onClick={handleLgConnect}>
                        Connect LG
                      </button>
                      <button type="button" className="console-remote__secondary-btn" onClick={handleLgWake}>
                        Wake LG
                      </button>
                    </div>
                  ) : null}

                  <div className="console-remote__action-grid">
                    {canPowerOn ? <button type="button" className="console-remote__primary-btn" onClick={handlePowerOn}>Power On</button> : null}
                    {canPowerOff ? <button type="button" className="console-remote__warning-btn" onClick={handlePowerOff}>Power Off</button> : null}
                    {showWolAction || selectedMethod === 'wol' ? <button type="button" className="console-remote__secondary-btn" onClick={handleWakeOnLan}>Wake LAN</button> : null}
                    {showWolAction || selectedMethod === 'wol' ? <button type="button" className="console-remote__warning-btn" onClick={handleSleepOnLan}>Sleep LAN</button> : null}
                    {isAppDevice ? <button type="button" className="console-remote__secondary-btn" onClick={handleLaunchSuitestream}>Suitestream</button> : null}
                  </div>

                  {canSwitchSource ? (
                    <div className="console-remote__form-row">
                      <select
                        className="console-remote__select"
                        value={selectedSource}
                        onChange={(event) => setSelectedSource(event.target.value)}
                      >
                        {hdmiAssignments.map((entry) => (
                          <option key={entry.label || entry.port} value={entry.label}>
                            {entry.label}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="console-remote__secondary-btn" onClick={handleSwitchSource}>
                        Source
                      </button>
                    </div>
                  ) : null}

                  {isAppDevice ? (
                    <div className="console-remote__form-row">
                      <select
                        className="console-remote__select"
                        value={selectedAppPackage}
                        onChange={(event) => setSelectedAppPackage(event.target.value)}
                        disabled={!orderedApps.length || appsStatus === 'loading'}
                      >
                        {!orderedApps.length ? (
                          <option value="">
                            {appsStatus === 'loading'
                              ? 'Loading apps…'
                              : installedExternalAppCount
                                ? 'No apps enabled'
                                : 'No apps reported'}
                          </option>
                        ) : null}
                        {orderedApps.map((app) => (
                          <option key={app.package_name} value={app.package_name}>
                            {app.app_name || app.package_name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="console-remote__primary-btn"
                        onClick={handleLaunchSelectedApp}
                        disabled={!selectedAppPackage || appsStatus === 'loading'}
                      >
                        Launch
                      </button>
                    </div>
                  ) : null}

                  {isAppDevice ? (
                    <div className="console-remote__shortcuts">
                      <div className="console-remote__shortcuts-header">
                        <strong>App shortcuts</strong>
                        <span>
                          {contentTargetsStatus === 'loading'
                            ? 'Loading…'
                            : `${contentTargetGroups.reduce((count, group) => count + group.targets.length, 0)} saved`}
                        </span>
                      </div>
                      {contentTargetGroups.map((group) => (
                        <div key={group.packageName} className="console-remote__shortcut-group">
                          <span>{group.appName}</span>
                          <div>
                            {group.targets.map((target) => (
                              <button
                                key={target.id}
                                type="button"
                                className="console-remote__secondary-btn"
                                onClick={() => handleExecuteContentTarget(target)}
                                disabled={targetExecution?.status === 'running'}
                              >
                                {targetExecution?.targetId === target.id
                                  && targetExecution.status === 'running'
                                  ? 'Starting…'
                                  : target.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      {contentTargetsStatus === 'ready' && !contentTargetGroups.length ? (
                        <div className="console-remote__notice">
                          No available shortcuts. Create favourites from Apps setup.
                        </div>
                      ) : null}
                      {targetExecution ? (
                        <div className={`console-remote__target-result is-${targetExecution.status}`}>
                          <span>{targetExecution.message}</span>
                          {targetExecution.status === 'failed' ? (
                            <button
                              type="button"
                              className="console-remote__secondary-btn"
                              onClick={() => {
                                const target = contentTargetGroups
                                  .flatMap((group) => group.targets)
                                  .find((entry) => entry.id === targetExecution.targetId);
                                if (target) handleExecuteContentTarget(target);
                              }}
                            >
                              Retry
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {statusMessage ? <div className="console-remote__notice">{statusMessage}</div> : null}
                  <div className="console-remote__runtime-line">
                    <span>{connectedMethodLabels.length ? connectedMethodLabels.join(', ') : 'WebOS / WOL fallback'}</span>
                    <strong>{lastAction || 'Ready'}</strong>
                  </div>
                </div>

                <div className="console-remote__panel console-remote__preview-panel">
                  <div className="console-remote__inline-actions">
                    <button type="button" className="console-remote__secondary-btn" onClick={() => setPreviewOpen((prev) => !prev)}>
                      {previewOpen ? 'Hide Preview' : 'Preview'}
                    </button>
                    {previewOpen && canPreviewSnapshot ? (
                      <button
                        type="button"
                        className="console-remote__primary-btn"
                        onClick={() => fetchLiveSnapshot('manual')}
                        disabled={snapshotStatus === 'loading' || snapshotStatus === 'refreshing'}
                      >
                        Refresh
                      </button>
                    ) : null}
                  </div>

                  {previewOpen ? (
                    canPreviewSnapshot ? (
                      <>
                        <div className="console-remote__preview-presets">
                          {Object.entries(PREVIEW_PRESETS).map(([key, preset]) => (
                            <button
                              key={key}
                              type="button"
                              className={`console-remote__chip${previewPreset === key ? ' is-active' : ''}`}
                              onClick={() => handlePreviewPresetChange(key)}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                        <div className="console-remote__preview-meta">
                          <span>{liveSnapshotLabel}</span>
                          <strong>Input {formatPreviewInterval(inputDelayMs)} • Background {formatPreviewInterval(backgroundIntervalMs)}</strong>
                        </div>
                        {previewImageUrl ? (
                          <div className="console-remote__preview-frame">
                            <img src={previewImageUrl} alt={`Live preview for ${device?.name || 'selected device'}`} className="console-remote__preview-image" />
                          </div>
                        ) : (
                          <div className="console-remote__preview-empty">{snapshotMessage || 'Preview not available.'}</div>
                        )}
                        {protectedVideoLikely ? <div className="console-remote__notice">Protected video may block preview.</div> : null}
                      </>
                    ) : (
                      <div className="console-remote__preview-empty">Preview is not supported for this target.</div>
                    )
                  ) : null}
                </div>
              </div>
            </section>

            {karaokeAvailable || jukeboxAvailable ? (
              <section className="console-remote__screen console-remote__screen--karaoke" aria-label="Live queue">
                <div className="console-remote__screen-inner console-remote__karaoke-shell">
                  {karaokeAvailable && jukeboxAvailable ? (
                    <div className="phone-remote__queue-tabs">
                      <button
                        type="button"
                        className={activeQueuePlugin === 'karaoke' ? 'phone-remote__queue-tab phone-remote__queue-tab--active' : 'phone-remote__queue-tab'}
                        onClick={() => setActiveQueuePlugin('karaoke')}
                      >
                        Karaoke
                      </button>
                      <button
                        type="button"
                        className={activeQueuePlugin === 'jukebox' ? 'phone-remote__queue-tab phone-remote__queue-tab--active' : 'phone-remote__queue-tab'}
                        onClick={() => setActiveQueuePlugin('jukebox')}
                      >
                        Jukebox
                      </button>
                    </div>
                  ) : null}
                  <KaraokeRemotePanel
                    compact
                    plugin={activeQueuePlugin === 'jukebox' && jukeboxAvailable ? 'jukebox' : karaokeAvailable ? 'karaoke' : 'jukebox'}
                  />
                </div>
              </section>
            ) : null}

            <section className="console-remote__screen console-remote__screen--controls" aria-label="Remote controls">
              <div className="console-remote__screen-inner console-remote__control-stage">
                <div className="console-remote__control-halo">
                  <div id="console-remote-install-prompt-slot" className="console-remote__install-prompt-slot" />
                  {device ? (
                    <UnifiedTvControl
                      key={`${device?.id}:${selectedMethod}`}
                      commands={UNIFIED_TV_COMMANDS}
                      onCommand={handlePrimaryRemoteCommand}
                      onInputEnd={handleRemoteInputEnd}
                      available={remoteAvailableCommands}
                    />
                  ) : (
                    <div className="console-remote__notice">Select a target in settings.</div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </RemotePwaSupport>
  );
}
